import { useTranslation } from 'react-i18next';

import { FormFieldCombobox } from '~/components/FormField';
import { useLabelGroupList } from '~/modules/settings/api/queries/labelGroupList';
import { RoleMap } from '~/modules/settings/constants/RoleMap';
import { DEFAULT_DROPDOWN_PAGE_SIZE } from '~settings/constants';

interface InternalPermissionsProps {
  isEditing?: boolean;
  name: string;
}

const INTERNAL_USER_ROLES = [
  'OPS_ADMIN',
  'TECH_ADMIN',
  'RELEASE_MANAGER',
  'RELEASE_COORDINATOR',
  'VIDEO_MANAGER',
  'SUPPORT_ADMIN',
] as const;

const mapRoles = (
  roles: Readonly<(keyof typeof RoleMap)[]>,
  t: (key: string) => string,
) =>
  roles.map((role) => ({
    value: role,
    label: t(RoleMap[role].name),
  }));

export function InternalPermissions({
  isEditing,
  name,
}: InternalPermissionsProps) {
  const { labelGroupList } = useLabelGroupList({
    skip: 0,
    take: DEFAULT_DROPDOWN_PAGE_SIZE,
    ...(isEditing ? { canEditUsers: true } : { canCreateUsers: true }),
  });

  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'roles' });
  const { t } = useTranslation('settings', { keyPrefix: 'user' });

  const labelGroups = labelGroupList?.data ?? [];

  return (
    <div className="grid grid-cols-3 items-start gap-md">
      <FormFieldCombobox
        name={`${name}.roles`}
        label={t('fields.roles')}
        placeholder={t('placeholder.roles')}
        multiselect
        displaySelectedOptions
        options={mapRoles(INTERNAL_USER_ROLES, rolesT)}
      />
      <FormFieldCombobox
        name={`${name}.labelGroups`}
        label={t('fields.labelGroups')}
        placeholder={t('placeholder.labelGroups')}
        multiselect
        displaySelectedOptions
        options={labelGroups.map((labelGroup) => ({
          label: labelGroup.name,
          value: labelGroup.id,
        }))}
      />
    </div>
  );
}

InternalPermissions.displayName = 'InternalPermissions';
